import { useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { logout, isAuthenticated } from "../utils/auth";

/**
 * Custom hook to handle user inactivity timeout
 * Automatically logs out user after specified minutes of inactivity
 *
 * @param {number} minutes - Minutes of inactivity before logout (default: 15)
 * @param {boolean} enabled - Whether the timer is enabled (default: true)
 */
export const useInactivityTimer = (minutes = 15, enabled = true) => {
  const navigate = useNavigate();
  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);

  // Convert minutes to milliseconds
  const timeoutDuration = minutes * 60 * 1000;

  // Function to reset the inactivity timer
  const resetTimer = useCallback(() => {
    // Clear existing timeout
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }

    // Only set timer if user is authenticated and timer is enabled
    if (enabled && isAuthenticated()) {
      // Set new timeout for logout
      timeoutRef.current = setTimeout(() => {
        // Logout user
        logout();

        // Navigate to login page
        navigate("/login", {
          replace: true,
          state: {
            message:
              "You have been logged out due to inactivity. Please login again.",
          },
        });
      }, timeoutDuration);
    }
  }, [enabled, navigate, timeoutDuration]);

  // Function to handle user activity
  const handleActivity = useCallback(() => {
    resetTimer();
  }, [resetTimer]);

  useEffect(() => {
    // Only set up timer if enabled and user is authenticated
    if (!enabled || !isAuthenticated()) {
      // Clear any existing timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      return;
    }

    // Set initial timer
    resetTimer();

    // List of events that indicate user activity
    const events = [
      "mousedown",
      "mousemove",
      "keypress",
      "scroll",
      "touchstart",
      "click",
    ];

    // Add event listeners for user activity
    events.forEach((event) => {
      document.addEventListener(event, handleActivity, true);
    });

    // Optional: Also reset timer on window focus (in case user switches tabs and comes back)
    const handleFocus = () => {
      if (isAuthenticated()) {
        resetTimer();
      }
    };
    window.addEventListener("focus", handleFocus);

    // Cleanup function
    return () => {
      // Remove event listeners
      events.forEach((event) => {
        document.removeEventListener(event, handleActivity, true);
      });
      window.removeEventListener("focus", handleFocus);

      // Clear timers
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [enabled, handleActivity, resetTimer]);

  // Return reset function in case it needs to be called manually
  return { resetTimer };
};
